import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import type { Provider } from 'app/generated/graphql'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { useLocationStore } from 'app/stores/StoreContext'
import { logError } from 'lib/logging'

const PROVIDERS_QUERY = graphql(`
  query AvailabilityProviders($search: String, $pagination: PaginationInput) {
    providers(search: $search, pagination: $pagination) {
      hits {
        id
        displayName
        deactivated
      }
      total
      page
    }
  }
`)

const UPDATE_PROVIDER_MUTATION = graphql(`
  mutation UpdateProviderStatus($id: ID!, $input: UpdateProviderInput!) {
    updateProvider(id: $id, input: $input) {
      id
      deactivated
      displayName
    }
  }
`)

const SYNC_PROVIDERS_MUTATION = graphql(`
  mutation SyncAvailabilityProviders($locationId: ID!) {
    syncProviders(locationId: $locationId) {
      success
      synced
      error
    }
  }
`)

function sortByStatus(items: Provider[]): Provider[] {
  const enabled = items.filter((p) => !p.deactivated)
  const disabled = items.filter((p) => p.deactivated)
  const byName = (a: Provider, b: Provider) => (a.displayName || ``).localeCompare(b.displayName || ``)
  return [...enabled.sort(byName), ...disabled.sort(byName)]
}

export type UseAvailabilityProvidersReturn = {
  rows: Provider[]
  isSyncing: boolean
  onSyncClick: () => void
  onStatusChange: (id: string, deactivated: boolean) => void
}

export function useAvailabilityProviders(): UseAvailabilityProvidersReturn {
  const { locationId } = useParams()
  const locationStore = useLocationStore()
  const [rows, setRows] = useState<Provider[]>([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [searchFilter, setSearchFilter] = useState<string>()
  const activeLocationIdRef = useRef(locationId)
  // When the user syncs before the filter has resolved, the providers query is still paused,
  // so a re-execute is dropped. Record that a refresh is due and fire it once the query is active.
  const pendingRefreshRef = useRef(false)

  const [, executeUpdate] = useMutation(UPDATE_PROVIDER_MUTATION)
  const [, executeSync] = useMutation(SYNC_PROVIDERS_MUTATION)

  const resolveFilter = useCallback((result: Awaited<ReturnType<typeof locationStore.fetchOne>>) => {
    const data = result.data as Record<string, unknown> | undefined
    const loc = (data?.location ?? data) as Record<string, string | undefined> | undefined
    if (!loc) return
    const consumerId = loc.ascendLocationId || loc.carestackId || loc.kollaConsumerId
    const vendor = loc.ascendLocationId ? `ascend` : loc.carestackId ? `carestack` : `kolla`
    if (consumerId) {
      setSearchFilter(`kollaConsumerId:${consumerId} vendor:${vendor} !archived:true`)
    }
  }, [])

  // Fetch location to determine the kollaConsumerId and vendor for filtering
  useEffect(() => {
    if (!locationId) return
    activeLocationIdRef.current = locationId
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        if (activeLocationIdRef.current !== locationId) return
        resolveFilter(result)
      })
      .catch((err) => logError(`Error fetching location for providers`, err))
  }, [locationId, locationStore, resolveFilter])

  const [providersResult, reexecuteProviders] = useQuery({
    query: PROVIDERS_QUERY,
    variables: { search: searchFilter, pagination: { limit: 200 } },
    pause: !searchFilter,
  })

  useEffect(() => {
    if (searchFilter && pendingRefreshRef.current) {
      pendingRefreshRef.current = false
      reexecuteProviders({ requestPolicy: `network-only` })
    }
  }, [searchFilter, reexecuteProviders])

  useEffect(() => {
    if (providersResult.data?.providers?.hits) {
      setRows(sortByStatus(providersResult.data.providers.hits as Provider[]))
    }
  }, [providersResult.data])

  const onSyncClick = useCallback(() => {
    if (!locationId) return
    setIsSyncing(true)
    executeSync({ locationId }, { url: OPS_URL })
      .then((result) => {
        const sync = result.data?.syncProviders
        if (sync?.success) {
          // A count of 0 is a valid result: either the practice has no providers, or none
          // qualified to sync (for example all inactive). Use wording that is true in both cases.
          if (sync.synced && sync.synced > 0) {
            notifySuccess(`Providers synced`)
          } else {
            notifySuccess(`No active providers to sync`)
          }
          if (searchFilter) {
            reexecuteProviders({ requestPolicy: `network-only` })
          } else {
            pendingRefreshRef.current = true
          }
        } else {
          notifyError(sync?.error || `Error syncing providers`)
        }
      })
      .catch(() => notifyError(`Error syncing providers`))
      .finally(() => setIsSyncing(false))
  }, [locationId, executeSync, reexecuteProviders, searchFilter])

  const onStatusChange = useCallback(
    (id: string, deactivated: boolean) => {
      executeUpdate({ id, input: { deactivated } })
        .then((result) => {
          if (!result.error) {
            notifySuccess(`Provider updated`)
            setRows((prev) => sortByStatus(prev.map((row) => (row.id === id ? { ...row, deactivated } : row))))
          } else {
            notifyError(`Error updating provider`)
          }
        })
        .catch(() => notifyError(`Error updating provider`))
    },
    [executeUpdate],
  )

  return { rows, isSyncing, onSyncClick, onStatusChange }
}
